/**
 * NL-014 funnel calculation receipt contract.
 *
 * This is the keystone that binds the already-shipped NL-010 seasonal-demand,
 * NL-014 decimal/price-evidence, and NL-041 package-rule contracts into the one
 * serializable receipt a later, caller-owned funnel calculation flow may
 * construct. It validates only caller-supplied metadata: it does not run the
 * funnel engine, price anything, resolve seasonal evidence, recommend a
 * package, read a runtime clock, or perform I/O.
 *
 * Two properties are structural, not conventional. Every receipt is a planning
 * estimate — the contract has no representation for a quote, a commitment, or
 * an approved price. And a component may only be declared "used" while naming
 * the exact upstream contract version this module understands, so an
 * unvalidated or blocked upstream artifact cannot be laundered into a ready
 * receipt by renaming it.
 */
import {
  DECIMAL_ROUNDING_CONTRACT_VERSION,
  MAX_DECIMAL_OUTPUT_SCALE,
} from "../calculator-contracts/decimal";
import { PRICE_EVIDENCE_RECEIPT_CONTRACT_VERSION } from "../calculator-contracts/price-evidence-receipt";
import { isCanonicalHash } from "./canonical-hash";
import { PACKAGE_RECOMMENDATION_RULE_VERSION } from "../packages/package-recommender";
import { SEASONAL_DEMAND_RECEIPT_CONTRACT_VERSION } from "./seasonality/receipts";
import { FUNNEL_STATE_VERSIONS, type FunnelStateVersion } from "./schema";

export const FUNNEL_CALCULATION_RECEIPT_CONTRACT_VERSION =
  "nl014-funnel-calculation-receipt-v1";

/**
 * The calculation-method and formula versions a funnel calculation receipt is
 * produced under. Pinning both makes the receipt self-describing: it names the
 * method and formula that generated it, exactly as the NL-014 price-evidence
 * receipt pins its `calculatorVersion`/`formulaVersion`.
 */
export const FUNNEL_CALCULATION_METHOD_VERSION = "nl014-funnel-calculation-v1";
export const FUNNEL_CALCULATION_FORMULA_VERSION = "nl014-funnel-formula-v1";

/**
 * The only classification this contract can express. A receipt describes what
 * a planning estimate consumed; it never asserts a commercial commitment.
 */
export const FUNNEL_CALCULATION_RECEIPT_CLASSIFICATION = "planning-estimate" as const;

export type FunnelCalculationReceiptClassification =
  typeof FUNNEL_CALCULATION_RECEIPT_CLASSIFICATION;

/** Component order is fixed so that results are stable across callers. */
export const FUNNEL_CALCULATION_COMPONENTS = [
  "seasonalDemand",
  "priceEvidence",
  "packageRecommendation",
] as const;

export type FunnelCalculationComponentName = (typeof FUNNEL_CALCULATION_COMPONENTS)[number];

export const FUNNEL_CALCULATION_COMPONENT_STATUSES = ["not-used", "used"] as const;

export type FunnelCalculationComponentStatus =
  (typeof FUNNEL_CALCULATION_COMPONENT_STATUSES)[number];

/**
 * The upstream contract version each component must name when it is used.
 * Anything else is a foreign artifact and blocks the receipt.
 */
const REQUIRED_COMPONENT_CONTRACT_VERSIONS: Readonly<
  Record<FunnelCalculationComponentName, string>
> = Object.freeze({
  seasonalDemand: SEASONAL_DEMAND_RECEIPT_CONTRACT_VERSION,
  priceEvidence: PRICE_EVIDENCE_RECEIPT_CONTRACT_VERSION,
  packageRecommendation: PACKAGE_RECOMMENDATION_RULE_VERSION,
});

/**
 * A privacy-safe reference to one upstream contract result. An unused component
 * must be explicit rather than omitted, and must carry no version or hash, so a
 * receipt cannot silently drop a dependency it actually consumed.
 */
export type FunnelCalculationComponentReference = {
  readonly status: FunnelCalculationComponentStatus;
  /** Required when used; must be null when not used. */
  readonly contractVersion: string | null;
  /** Required when used; must be null when not used. */
  readonly receiptHash: string | null;
};

export type FunnelCalculationComponents = Readonly<
  Record<FunnelCalculationComponentName, FunnelCalculationComponentReference>
>;

/**
 * A privacy-safe reference to the computed estimate result. The canonical
 * result stays with the caller; the receipt retains only its hash and the unit
 * needed to interpret it, mirroring the NL-014 price-evidence result reference.
 */
export type FunnelCalculationResultReference = {
  readonly resultHash: string;
  readonly unit: string;
};

/**
 * The serializable receipt shape. It carries no money amounts, demand volumes,
 * contact details, or calculation inputs — only hashes and version pins.
 */
export type FunnelCalculationReceipt = {
  readonly classification: FunnelCalculationReceiptClassification;
  readonly funnelStateVersion: FunnelStateVersion;
  readonly decimalRoundingContractVersion: typeof DECIMAL_ROUNDING_CONTRACT_VERSION;
  readonly outputScale: number;
  /** The calculation method this receipt was produced under. */
  readonly calculatorVersion: string;
  /** The formula version this receipt was produced under. */
  readonly formulaVersion: string;
  /** Canonical UTC timestamp (`YYYY-MM-DDTHH:mm:ssZ`), supplied by the caller. */
  readonly calculationTimestamp: string;
  /** Canonical UTC calendar date (`YYYY-MM-DD`) the evidence is anchored to. */
  readonly evidenceDate: string;
  /**
   * The configuration/inputs hash. For this contract `inputsHash` IS the
   * configuration hash — there is deliberately no separate `configurationHash`
   * field, which would be redundant.
   */
  readonly inputsHash: string;
  readonly outputsHash: string;
  readonly result: FunnelCalculationResultReference;
  readonly components: FunnelCalculationComponents;
};

export type FunnelCalculationReceiptValidationInput = {
  /** `unknown` keeps this safe at future serialized-data boundaries. */
  readonly receipt?: unknown;
  /** A caller-supplied UTC calendar date; this module never reads the clock. */
  readonly asOfDate: string;
};

export type FunnelCalculationReceiptBlocker =
  | "missing-funnel-calculation-receipt"
  | "invalid-as-of-date"
  | "unrecognized-funnel-calculation-receipt-field"
  | "invalid-receipt-classification"
  | "unsupported-funnel-state-version"
  | "unsupported-decimal-contract-version"
  | "invalid-output-scale"
  | "missing-calculator-version"
  | "missing-formula-version"
  | "invalid-calculation-timestamp"
  | "calculation-after-as-of-date"
  | "invalid-evidence-date"
  | "evidence-date-after-as-of-date"
  | "missing-inputs-hash"
  | "missing-outputs-hash"
  | "raw-value-in-receipt-hash"
  | "non-hash-format-value"
  | "missing-result-reference"
  | "unrecognized-result-field"
  | "missing-result-hash"
  | "raw-value-in-result-hash"
  | "missing-result-unit"
  | "missing-calculation-components"
  | "unrecognized-calculation-component"
  | "missing-component-reference"
  | "unrecognized-calculation-component-field"
  | "invalid-component-status"
  | "unsupported-component-contract-version"
  | "unused-component-must-not-claim-contract-version"
  | "missing-component-receipt-hash"
  | "unused-component-must-not-claim-receipt-hash"
  | "raw-value-in-component-receipt-hash";

export type FunnelCalculationReceiptValidationResult = {
  readonly contractVersion: typeof FUNNEL_CALCULATION_RECEIPT_CONTRACT_VERSION;
  readonly readiness: "ready" | "blocked";
  /** Deduplicated in first-seen order; the three components share blockers. */
  readonly blockers: readonly FunnelCalculationReceiptBlocker[];
  readonly classification: FunnelCalculationReceiptClassification | null;
  /** Populated only for a ready receipt, in `FUNNEL_CALCULATION_COMPONENTS` order. */
  readonly usedComponents: readonly FunnelCalculationComponentName[];
};

type UnknownRecord = Readonly<Record<string, unknown>>;

const ISO_CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_UTC_TIMESTAMP = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})Z$/;
/** A hash slot holding a plain numeric string is a raw estimate value leak. */
const RAW_NUMERIC_VALUE = /^[+-]?\d+(?:[.,]\d+)?$/;

const RECEIPT_FIELDS = [
  "classification",
  "funnelStateVersion",
  "decimalRoundingContractVersion",
  "outputScale",
  "calculatorVersion",
  "formulaVersion",
  "calculationTimestamp",
  "evidenceDate",
  "inputsHash",
  "outputsHash",
  "result",
  "components",
] as const;

const RESULT_FIELDS = ["resultHash", "unit"] as const;

const COMPONENT_REFERENCE_FIELDS = ["status", "contractVersion", "receiptHash"] as const;

function asRecord(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

function rejectUnrecognizedFields(
  record: UnknownRecord,
  allowedFields: readonly string[],
  blocker: FunnelCalculationReceiptBlocker,
  blockers: FunnelCalculationReceiptBlocker[],
): void {
  if (Object.keys(record).some((field) => !allowedFields.includes(field))) {
    blockers.push(blocker);
  }
}

function nonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null;

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function isCalendarDate(value: string | null): value is string {
  if (!value) return false;

  const match = ISO_CALENDAR_DATE.exec(value);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const candidate = new Date(Date.UTC(year, month - 1, day));

  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  );
}

function isCanonicalUtcTimestamp(value: string | null): value is string {
  if (!value) return false;

  const match = ISO_UTC_TIMESTAMP.exec(value);
  if (!match || !isCalendarDate(match[1])) return false;

  const hour = Number(match[2]);
  const minute = Number(match[3]);
  const second = Number(match[4]);

  return hour <= 23 && minute <= 59 && second <= 59;
}

function isFunnelStateVersion(value: unknown): value is FunnelStateVersion {
  return (
    typeof value === "number" &&
    (FUNNEL_STATE_VERSIONS as readonly number[]).includes(value)
  );
}

function isOutputScale(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= MAX_DECIMAL_OUTPUT_SCALE
  );
}

function isComponentStatus(value: unknown): value is FunnelCalculationComponentStatus {
  return (
    typeof value === "string" &&
    (FUNNEL_CALCULATION_COMPONENT_STATUSES as readonly string[]).includes(value)
  );
}

function validateReceiptHash(
  value: unknown,
  missingBlocker: FunnelCalculationReceiptBlocker,
  leakBlocker: FunnelCalculationReceiptBlocker,
  blockers: FunnelCalculationReceiptBlocker[],
): void {
  const hash = nonEmptyString(value);

  if (!hash) {
    blockers.push(missingBlocker);
    return;
  }

  // A bare figure in a hash slot is a leaked estimate value, and is reported as
  // such rather than as a generic format problem — the distinction tells an
  // operator whether a number escaped or a slot was simply filled wrongly.
  if (RAW_NUMERIC_VALUE.test(hash)) blockers.push(leakBlocker);

  // Everything else is held to the exact canonical digest shape. This is a
  // positive check on purpose: rejecting only "obviously not a hash" patterns
  // let phone numbers, URLs, hostnames and `1e5` through, so the contract could
  // not actually prove a hash was a hash.
  if (!isCanonicalHash(hash)) blockers.push("non-hash-format-value");
}

/**
 * Validates the privacy-safe result reference. The result hash is held to the
 * same guard as the input/output hashes, so a raw value or free-text/PII string
 * in the result slot is rejected just as it is anywhere else in the receipt.
 */
function validateResultReference(
  value: unknown,
  blockers: FunnelCalculationReceiptBlocker[],
): void {
  const result = asRecord(value);
  if (!result) {
    blockers.push("missing-result-reference");
    return;
  }

  rejectUnrecognizedFields(result, RESULT_FIELDS, "unrecognized-result-field", blockers);

  validateReceiptHash(
    result.resultHash,
    "missing-result-hash",
    "raw-value-in-result-hash",
    blockers,
  );
  if (!nonEmptyString(result.unit)) blockers.push("missing-result-unit");
}

/**
 * Returns the component status when the reference is internally consistent, so
 * the caller can report which components a ready receipt actually consumed.
 */
function validateComponentReference(
  value: unknown,
  component: FunnelCalculationComponentName,
  blockers: FunnelCalculationReceiptBlocker[],
): FunnelCalculationComponentStatus | null {
  const reference = asRecord(value);
  if (!reference) {
    blockers.push("missing-component-reference");
    return null;
  }

  rejectUnrecognizedFields(
    reference,
    COMPONENT_REFERENCE_FIELDS,
    "unrecognized-calculation-component-field",
    blockers,
  );

  if (!isComponentStatus(reference.status)) {
    blockers.push("invalid-component-status");
    return null;
  }

  if (reference.status === "not-used") {
    if (reference.contractVersion !== null) {
      blockers.push("unused-component-must-not-claim-contract-version");
    }
    if (reference.receiptHash !== null) {
      blockers.push("unused-component-must-not-claim-receipt-hash");
    }

    return "not-used";
  }

  if (nonEmptyString(reference.contractVersion) !== REQUIRED_COMPONENT_CONTRACT_VERSIONS[component]) {
    blockers.push("unsupported-component-contract-version");
  }

  validateReceiptHash(
    reference.receiptHash,
    "missing-component-receipt-hash",
    "raw-value-in-component-receipt-hash",
    blockers,
  );

  return "used";
}

function dedupe(
  blockers: readonly FunnelCalculationReceiptBlocker[],
): readonly FunnelCalculationReceiptBlocker[] {
  return [...new Set(blockers)];
}

/**
 * Validates a caller-supplied funnel calculation receipt deterministically.
 * Unknown fields, foreign contract versions, unused components claiming
 * provenance, and raw values in hash slots all block. A ready result is
 * provenance metadata for a planning estimate only: it authorizes no pricing,
 * CRM, or activation action.
 */
export function validateFunnelCalculationReceipt(
  input: FunnelCalculationReceiptValidationInput,
): FunnelCalculationReceiptValidationResult {
  const blockers: FunnelCalculationReceiptBlocker[] = [];
  const asOfDate = nonEmptyString(input.asOfDate);

  if (!isCalendarDate(asOfDate)) blockers.push("invalid-as-of-date");

  const receipt = asRecord(input.receipt);
  if (!receipt) {
    blockers.push("missing-funnel-calculation-receipt");

    return {
      contractVersion: FUNNEL_CALCULATION_RECEIPT_CONTRACT_VERSION,
      readiness: "blocked",
      blockers: dedupe(blockers),
      classification: null,
      usedComponents: [],
    };
  }

  rejectUnrecognizedFields(
    receipt,
    RECEIPT_FIELDS,
    "unrecognized-funnel-calculation-receipt-field",
    blockers,
  );

  const calculationTimestamp = nonEmptyString(receipt.calculationTimestamp);
  const evidenceDate = nonEmptyString(receipt.evidenceDate);

  if (receipt.classification !== FUNNEL_CALCULATION_RECEIPT_CLASSIFICATION) {
    blockers.push("invalid-receipt-classification");
  }
  if (!isFunnelStateVersion(receipt.funnelStateVersion)) {
    blockers.push("unsupported-funnel-state-version");
  }
  if (receipt.decimalRoundingContractVersion !== DECIMAL_ROUNDING_CONTRACT_VERSION) {
    blockers.push("unsupported-decimal-contract-version");
  }
  if (!isOutputScale(receipt.outputScale)) blockers.push("invalid-output-scale");
  if (!nonEmptyString(receipt.calculatorVersion)) blockers.push("missing-calculator-version");
  if (!nonEmptyString(receipt.formulaVersion)) blockers.push("missing-formula-version");
  if (!isCanonicalUtcTimestamp(calculationTimestamp)) {
    blockers.push("invalid-calculation-timestamp");
  } else if (isCalendarDate(asOfDate) && calculationTimestamp.slice(0, 10) > asOfDate) {
    blockers.push("calculation-after-as-of-date");
  }
  if (!isCalendarDate(evidenceDate)) {
    blockers.push("invalid-evidence-date");
  } else if (isCalendarDate(asOfDate) && evidenceDate > asOfDate) {
    blockers.push("evidence-date-after-as-of-date");
  }

  validateReceiptHash(
    receipt.inputsHash,
    "missing-inputs-hash",
    "raw-value-in-receipt-hash",
    blockers,
  );
  validateReceiptHash(
    receipt.outputsHash,
    "missing-outputs-hash",
    "raw-value-in-receipt-hash",
    blockers,
  );
  validateResultReference(receipt.result, blockers);

  const usedComponents: FunnelCalculationComponentName[] = [];
  const components = asRecord(receipt.components);

  if (!components) {
    blockers.push("missing-calculation-components");
  } else {
    rejectUnrecognizedFields(
      components,
      FUNNEL_CALCULATION_COMPONENTS,
      "unrecognized-calculation-component",
      blockers,
    );

    for (const component of FUNNEL_CALCULATION_COMPONENTS) {
      if (validateComponentReference(components[component], component, blockers) === "used") {
        usedComponents.push(component);
      }
    }
  }

  const ready = blockers.length === 0;

  return {
    contractVersion: FUNNEL_CALCULATION_RECEIPT_CONTRACT_VERSION,
    readiness: ready ? "ready" : "blocked",
    blockers: dedupe(blockers),
    classification: ready ? FUNNEL_CALCULATION_RECEIPT_CLASSIFICATION : null,
    usedComponents: ready ? usedComponents : [],
  };
}
